import * as React from "react";
import { useCallback } from "react";
import { useTranslations } from "../../i18n/useTranslations";
import { useMenu } from "../../Menu/useMenu";
import { useUser } from "../../user/useUser";
import { useUserMenu } from "../../UserMenu/useUserMenu";
import {
	getCurrentUrl,
	getInitial,
	mapBooleanToOpenClose,
	withRedirectTo,
} from "../../utils";
import { CircleCloseButton } from "./CircleCloseButton";

const mapBooleanToLoggedInOut = (x: boolean) =>
	x ? "logged-in" : "logged-out";

export const UserButton = () => {
	const { loggedIn, firstName } = useUser();
	const { login } = useMenu().items;
	const { toggleUserMenu, isUserMenuOpen } = useUserMenu();
	const { t } = useTranslations();

	const onClickOnLogin = useCallback(
		(e: React.MouseEvent<HTMLAnchorElement>) => {
			e.preventDefault();

			const withRedirect = withRedirectTo(getCurrentUrl());
			document.location.href = withRedirect(login.href);
		},
		[login.href],
	);

	const onClickOnLoginUserIcon = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e.stopPropagation();
			toggleUserMenu();
		},
		[toggleUserMenu],
	);

	return (
		<div
			className={
				loggedIn
					? "elm-w-10 elm-h-10 elm-rounded-full elm-pointer-events-auto elm-cursor-pointer elm-transition lg:hover:elm-bg-dd-blue-100"
					: "elm-flex elm-items-center elm-h-10 elm-pointer-events-auto elm-cursor-pointer"
			}
		>
			{!loggedIn && (
				<a
					className="focus:elm-outline-none elm-flex elm-items-center elm-h-10 elm-px-1 elm-whitespace-nowrap elm-text-dd-blue-900 elm-text-xs md:elm-text-sm elm-font-medium elm-no-underline"
					tabIndex={0}
					data-track-label="link:user:login"
					href={login.href}
					target="_parent"
					title={login.label}
					onClick={onClickOnLogin}
					aria-label={login.label}
				>
					{login.label}
				</a>
			)}
			{loggedIn && (
				<button
					tabIndex={0}
					className="focus:elm-outline-none elm-p-0 elm-border-0 elm-bg-transparent elm-rounded-full"
					data-track-label={`btn:user:${mapBooleanToLoggedInOut(
						loggedIn,
					)}:${mapBooleanToOpenClose(!isUserMenuOpen)}`}
					onClick={onClickOnLoginUserIcon}
					title={t("User menu")}
					aria-label={t("User menu")}
				>
					{isUserMenuOpen ? (
						<CircleCloseButton />
					) : (
						<LoggedInState letter={getInitial(firstName)} />
					)}
				</button>
			)}
		</div>
	);
};

function LoggedInState({ letter }: { letter: string }) {
	return (
		<div className="elm-w-10 elm-h-10 elm-flex elm-justify-center elm-items-center elm-pointer-events-none">
			<div className="elm-font-sans elm-bg-dd-blue-600 elm-w-6.5 elm-h-6.5 elm-text-base elm-rounded-full elm-flex elm-justify-center elm-items-center elm-font-semibold elm-text-white">
				{letter}
			</div>
		</div>
	);
}
